import type { FC, PropsWithChildren, ReactNode } from 'react';
import { Alert, Flex } from '@chakra-ui/react';
import PropTypes from 'better-prop-types';
import { Text } from '../../ui-components';
import { classNames } from '../../utils/tailwind/classNames';
import { WarningIcon } from '../icons';

interface BasicErrorOptions {
    alertDataTestId?: string;
    errorMessageDataTestId?: string;
    showOutline?: boolean;
    iconVerticalAlign?: 'top' | 'middle';
}

type BasicErrorProps = PropsWithChildren<{
    extra?: ReactNode;
    options?: BasicErrorOptions;
    margin?: string;
}>;

export const BasicError: FC<BasicErrorProps> = ({ children, extra, options, margin }) => {
    const {
        showOutline = true,
        alertDataTestId,
        errorMessageDataTestId,
        iconVerticalAlign = 'middle',
    } = options ?? {};
    return (
        <Alert
            data-testid={alertDataTestId ?? 'basic-error-container'}
            status="error"
            padding="0.5rem"
            margin={margin ?? '0.5rem 0'}
            borderColor="slt-red.400"
            borderRadius="0.25rem"
            borderWidth="1px"
            borderStyle={showOutline ? 'solid' : 'none'}
        >
            <div className="flex w-full flex-row justify-between">
                <div
                    className={classNames('flex flex-row space-x-2', {
                        'items-start': iconVerticalAlign === 'top',
                    })}
                >
                    <Flex alignItems="start" marginTop={1}>
                        <WarningIcon width="18px" height="16px" color="slt-red-error" />
                    </Flex>
                    <Text
                        color="slt-red-error"
                        id={errorMessageDataTestId ?? 'basic-error-message'}
                    >
                        {children}
                    </Text>
                </div>
                {extra || null}
            </div>
        </Alert>
    );
};

BasicError.propTypes = {
    children: PropTypes.node,
    extra: PropTypes.node,
    options: PropTypes.shape({
        alertDataTestId: PropTypes.string,
        errorMessageDataTestId: PropTypes.string,
        showOutline: PropTypes.bool,
        iconVerticalAlign: PropTypes.oneOf(['top', 'middle']),
    }),
};

BasicError.defaultProps = {
    children: null,
    extra: null,
    options: undefined,
    margin: undefined,
};
